import { H3, ProductCarousel, Spinner } from '@ahmdigital/ui';
import { includes } from 'lodash/fp';
import { RefObject, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { BuildYourCoverProductType } from '../../../../../../actions/build-your-cover';
import { LoadingContainer, SmallErrorText, StyledSection } from '../../styles';
import { ProductType } from '../../../../../../types/product';
import { RootState } from '../../../../../../reducers';
import getCarouselStartIndex from '../../../../utils/get-carousel-start-index';
import PaymentToggles from '../../../components/payment-toggles';
import RemoveProductType from './remove-product-type';
import selectCustomer from '../../../../../../selectors/select-customer';
import useCarouselTiles from '../carousel-tiles';
import useGetRecommendedProductsByAgeAndScale from '../../../../utils/use-get-recommended-products';

const LoadingScreen = () => (
  <LoadingContainer aria-busy="true" aria-live="polite" role="status" aria-label="Loading products">
    <Spinner hasLabel customLabel="Loading products" />
  </LoadingContainer>
);

type CarouselSectionProps = {
  hasProductWithFewerInclusions: boolean;
  isStuck: boolean;
  products: ProductType[];
  productType: BuildYourCoverProductType;
  sectionRef: RefObject<HTMLDivElement>;
};

const CarouselSection = ({
  hasProductWithFewerInclusions,
  isStuck,
  productType,
  products,
  sectionRef,
}: CarouselSectionProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { members, scale } = useSelector(selectCustomer);

  const { selectedServices, selectedProducts, invalidProductTypes } = useSelector(
    (state: RootState) => state.buildYourCover,
  );

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMoveLeft = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleMoveRight = () => {
    if (activeIndex < products.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const recommendedProducts = useGetRecommendedProductsByAgeAndScale(members?.[0]?.dob!, scale, selectedServices);

  const carouselTiles = useCarouselTiles({
    handleToggle,
    hasProductWithFewerInclusions,
    isExpanded,
    isStuck,
    products,
  });

  useEffect(() => {
    setActiveIndex(getCarouselStartIndex(recommendedProducts[productType], products, selectedProducts[productType]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedProducts[productType], products?.length]);

  return (
    <StyledSection ref={sectionRef}>
      <H3 looksLike="h5">Select {productType} cover</H3>
      {includes(productType, invalidProductTypes) && (
        <SmallErrorText>Please select {productType} cover to continue.</SmallErrorText>
      )}
      <PaymentToggles productType={productType} />

      {products?.length ? (
        <>
          <ProductCarousel
            productCount={products.length}
            isExpanded={isExpanded}
            isStuck={isStuck}
            activeIndex={activeIndex}
            handleMoveLeft={handleMoveLeft}
            handleMoveRight={handleMoveRight}
          >
            {carouselTiles}
          </ProductCarousel>
          <RemoveProductType productType={productType} />
        </>
      ) : (
        <LoadingScreen />
      )}
    </StyledSection>
  );
};

export default CarouselSection;
